import { Navigate, useLocation } from 'react-router-dom'
import PageLoader from '@/components/common/PageLoader'
import { useAppSelector } from '@/hooks/useAppSelector'
import {
  selectIsAuthenticated,
  selectIsChatGPTEnabled,
  selectIsLoading,
  selectIsSessionVerified,
} from '@/features/auth/authSlice'

/**
 * Render the ChatGPT account-linking continuation only while ChatGPT is
 * enabled for the signed-in user (`/me.chatgpt_enabled`); otherwise
 * `fallback` (the standard 404). A visitor who is not signed in goes to
 * sign-in first, carrying the full continuation, exactly as the backend's own
 * redirect does; the flag is known once the session is.
 *
 * @param {Object} props
 * @param {JSX.Element} props.element - rendered when ChatGPT is enabled
 * @param {JSX.Element} props.fallback - rendered when it is not
 * @returns {JSX.Element}
 */
export default function ChatGPTRouteGuard({ element, fallback }) {
  const location = useLocation()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isLoading = useAppSelector(selectIsLoading)
  const isSessionVerified = useAppSelector(selectIsSessionVerified)
  const isChatGPTEnabled = useAppSelector(selectIsChatGPTEnabled)

  if (!isAuthenticated) {
    if (isLoading && !isSessionVerified) return <PageLoader />
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (isChatGPTEnabled) return element
  // A rehydrated session may carry a stale user: wait for /auth/me before a 404.
  if (!isSessionVerified) return <PageLoader />
  return fallback
}
